import { DictType } from "@pma/types"
import useTranslator from "@hooks/use-translator"
import { cn, formatTime, getUserFromId, parseMessage } from "@pma/utils/helpers"
import { formatDistance } from "date-fns"
import { he } from "date-fns/locale"
import { UserIcon } from "lucide-react"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import { Avatar, AvatarFallback, AvatarImage } from "@components/common/avatar"
import { useAuth } from "@stores/auth-store"
import { useMemo } from "react"
import { changeTypes } from "@pma/utils/data"

interface Props {
  activity: DictType
  reverse: boolean
}
const ActivityCard = ({ activity, reverse }: Props) => {
  const { locale, translate } = useTranslator()
  const { authConfig } = useAuth()

  const isHebrew = locale === "he"

  const organizationUsers = authConfig?.organizationUsers

  const hasChanges = activity?.changes?.changes && activity?.changes?.changes?.length > 0
  const changes: DictType = activity?.changes?.changes ? activity?.changes?.changes[0] : {}
  const prevValue = changes?.prev_value
  const newValue = changes?.new_value

  const taskId = activity?.task_id
  const changeId = activity?.changes?.change_type_id
  const changeType = changeTypes[changeId]

  const parsedActivity: DictType = useMemo(() => {
    let temp = {}
    let valueKeys: any[] = []

    if (prevValue && Object.keys(prevValue).length > 0) {
      valueKeys = [...Object.keys(prevValue)]
    }
    if (newValue && Object.keys(newValue)?.length > 0) {
      valueKeys = [...Object.keys(newValue)]
    }

    if (valueKeys.includes("param_name")) {
      temp = {
        name: "status",
        from: prevValue?.name ? prevValue?.name?.replaceAll("_", " ") : prevValue?.name,
        to: newValue?.name ? newValue?.name?.replaceAll("_", " ") : newValue?.name,
        formColor: prevValue?.color,
        toColor: newValue?.color,
      }
    } else if (valueKeys.includes("title")) {
      temp = {
        name: "title",
        from: prevValue?.title,
        to: newValue?.title,
      }
    } else if (valueKeys.includes("file")) {
      temp = {
        name: "file was added",
        from: prevValue?.file,
        to: newValue?.file,
      }
    } else if (valueKeys.includes("note") || valueKeys.includes("Note")) {
      temp = {
        name: "note",
        from: parseMessage(prevValue?.note, organizationUsers),
        to: parseMessage(newValue?.note, organizationUsers),
      }
    } else if (valueKeys.includes("finish_to") || valueKeys.includes("start_at")) {
      const changesObj = activity.changes?.changes ? activity.changes?.changes[0] : null

      if (changesObj) {
        const changeAction = changesObj.change_action

        if (changeAction[locale] === "start time was changed") {
          const oldTime = changesObj.prev_value.start_at
            ? new Date(changesObj.prev_value.start_at)
            : null
          const newTime = changesObj.new_value?.start_at
            ? new Date(changesObj.new_value?.start_at)
            : null

          if (oldTime && newTime) {
            temp = {
              name: "start date",
              from: `${oldTime.getDate()}/${oldTime.getMonth() + 1}/${oldTime.getFullYear()}`,
              to: `${newTime.getDate()}/${newTime.getMonth() + 1}/${newTime.getFullYear()}`,
            }
          }
        } else if (changeAction[locale] === "finish time was changed") {
          const oldTime = changesObj.prev_value.finish_to
            ? new Date(changesObj.prev_value.finish_to)
            : null
          const newTime = changesObj.new_value?.finish_to
            ? new Date(changesObj.new_value?.finish_to)
            : null

          if (oldTime && newTime) {
            temp = {
              name: "finish date",
              from: `${oldTime.getDate()}/${oldTime.getMonth() + 1}/${oldTime.getFullYear()}`,
              to: `${newTime.getDate()}/${newTime.getMonth() + 1}/${newTime.getFullYear()}`,
            }
          }
        }
      }
    } else if (changes?.column_name === "approval") {
      temp = {
        name: "approval was changed",
        from: prevValue?.title,
        to: newValue?.title,
      }
    } else if (valueKeys.includes("description")) {
      temp = {
        name: "",
        from: prevValue?.description,
        to: newValue?.description,
      }
    } else if (changes?.column_name === "status_id") {
      temp = {
        name: "status",
        from: prevValue?.en,
        to: newValue?.en,
        formColor: prevValue?.color,
        toColor: newValue?.color,
      }
    } else {
      const changeObj = hasChanges ? activity?.changes?.changes[0] : null
      const changeAction = changeObj?.change_action ?? null

      if (changeAction && changeAction[locale]) {
        if (changeAction[locale] === "new project member") {
          temp = {
            name: changeAction[locale],
            to: newValue ? newValue.name : "",
          }
        } else {
          temp = {
            name: changeAction[locale],
            from: prevValue?.name || "",
            to: newValue?.name || "",
          }
        }
      }
    }

    return {
      ...temp,
      createdAt: activity?.created_at,
      updatedAt: activity?.changes_time,
    }
  }, [])

  const responsibleUser = getUserFromId(activity?.changes?.by_user, organizationUsers)

  return (
    <div
      className={`flex items-center rounded-lg border-gray-200 p-4 ${
        reverse ? "flex-row-reverse" : "flex-row"
      }`}
    >
      <div
        className={cn(
          "flex min-h-[100px] w-full flex-col rounded-lg border-b p-4 shadow-md md:w-1/2",
          reverse ? "border-l" : "border-r",
        )}
      >
        <div className='flex flex-1'>
          <div className='hidden md:flex'>
            <img
              src={
                responsibleUser?.user_picture?.url || `${process.env.PUBLIC_URL}/user-profile.svg`
              }
              alt={responsibleUser?.name}
              aria-hidden='true'
              className='mr-1 h-16 w-16 rounded-full'
            />
          </div>
          <div className='flex w-full flex-col'>
            <div className='flex flex-col'>
              <div className='flex h-fit items-center gap-1 py-0 text-xs'>
                <span className='text-nowrap font-semibold'> {responsibleUser?.name} </span>
                <div className='flex w-32 items-center gap-2 font-semibold text-primary'>
                  <UserIcon size={16} className='text-primary' />
                  <span>
                    {`${translate(changeType?.meta?.action) || "Edited task"} ${translate(changeType?.meta?.entity) || ""}`}
                  </span>
                </div>
                {!taskId && (
                  <div className='w-full text-wrap'>
                    {changes?.column_name?.name || translate("project")}
                  </div>
                )}
              </div>

              <div className='mt-1 flex h-5 items-center gap-4'>
                <p className='max-w-[25ch] truncate text-ellipsis md:max-w-[27ch] lg:max-w-[42ch] xl:max-w-[61ch] 2xl:max-w-[93ch]'>
                  {taskId ? translate("Task") : translate("Project")} {parsedActivity?.name}&nbsp;
                  {parsedActivity?.from && (
                    <>
                      {parsedActivity?.to ? "from" : ""}{" "}
                      <span
                        className={`rounded-full px-4 py-[2px] text-sm font-normal ${!taskId ? "px-0 text-primary" : ""}`}
                        style={{ background: parsedActivity?.formColor || "#transparent" }}
                      >
                        {parsedActivity?.from}
                      </span>
                      {parsedActivity?.to ? "to" : ""}{" "}
                    </>
                  )}
                  {parsedActivity?.to && (
                    <>
                      {parsedActivity?.name === "note" && (
                        <span>
                          {!parsedActivity?.from && parsedActivity?.to
                            ? translate("was added")
                            : translate("was updated")}
                          &nbsp;
                        </span>
                      )}

                      {parsedActivity?.name !== "status" ? (
                        <span
                          dangerouslySetInnerHTML={{
                            __html: parseMessage(parsedActivity?.to || "", organizationUsers),
                          }}
                          className='ml-1 truncate text-ellipsis text-primary'
                        />
                      ) : (
                        <span
                          className='rounded-full px-4 py-[2px] text-sm font-normal'
                          style={{ background: parsedActivity?.toColor || "#transparent" }}
                        >
                          {parsedActivity?.to}
                        </span>
                      )}
                    </>
                  )}
                </p>
              </div>
            </div>

            <div className='flex w-full cursor-pointer items-center justify-end'>
              {activity?.recepients?.map((user: DictType, index: number) => (
                <Tooltip key={index} delayDuration={0}>
                  <TooltipTrigger asChild>
                    <div className={""}>
                      <Avatar className={`ml-[-10px] h-7 w-7`}>
                        <AvatarImage
                          src={
                            user.user_picture
                              ? user.user_picture.url
                              : `${process.env.PUBLIC_URL}/usr.svg`
                          }
                          alt='Avatar'
                        />
                        <AvatarFallback className='uppercase'>
                          {user?.first_name && user?.first_name[0]}
                          {user?.last_name && user?.last_name[0]}
                        </AvatarFallback>
                      </Avatar>
                      <span className='sr-only'>{user.name}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side='bottom' className='flex items-center gap-4'>
                    {user.name}
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          </div>
        </div>

        <div className={`flex items-end justify-end pr-[50x] text-slate-500`}>
          <p>
            <small>
              {formatDistance(parsedActivity?.createdAt, new Date(), {
                locale: isHebrew ? he : undefined,
                addSuffix: true,
              })}
            </small>{" "}
            <small> @ {formatTime(parsedActivity?.createdAt, { amPm: true })} </small>{" "}
          </p>
        </div>
      </div>
    </div>
  )
}

export default ActivityCard
